/**
 * useContractorsByCategory Hook
 *
 * React hook for fetching contractors by category
 */

'use client';

import { useState, useCallback } from 'react';
import { fetchContractorsByCategories } from '../../api';
import type { ContractorCardProps } from '../../ui/ContractorCard';

export interface UseContractorsByCategoryReturn {
  contractors: ContractorCardProps[];
  loading: boolean;
  error: string | null;
  fetchContractors: (categoryIds: string[], useMock?: boolean) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for fetching contractors by category IDs
 */
export function useContractorsByCategory(): UseContractorsByCategoryReturn {
  const [contractors, setContractors] = useState<ContractorCardProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContractors = useCallback(
    async (categoryIds: string[], useMock = true) => {
      if (categoryIds.length === 0) {
        setContractors([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await fetchContractorsByCategories({
          categoryIds,
          useMock,
        });
        setContractors(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setContractors([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setContractors([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    contractors,
    loading,
    error,
    fetchContractors,
    reset,
  };
}
